/**
 * The mouse half of the control mapping (user request, 2026-08-19): the wheel
 * is the throttle lever and the primary button is the trigger. The *secondary*
 * button is free look (user request, 2026-08-25) and is handled next door in
 * `free-look-input.ts` — deliberately not here, because what it produces is a
 * camera and a `PointerFrame` is a sim input. Same split as
 * `keyboard.ts` / `keys.ts` — everything decision-shaped (how much throttle a
 * wheel notch is worth, and whether a pointer landed on the 3D viewport or on
 * a piece of chrome sitting over it) lives in `keys.ts` and is unit-tested;
 * this file is listeners and two accumulators.
 *
 * Listeners sit on `window` rather than on the canvas so a drag that starts
 * over the viewport and ends anywhere still releases the trigger, but every
 * event is filtered through `isViewportPointerTarget` first, so a click on the
 * HUD, the settings popover or the exit link is never a trigger pull and a
 * wheel over them still scrolls whatever they are.
 */

import { classChainOf, isViewportPointerTarget, throttleWheelDelta } from "./keys.ts";

/** One frame's mouse contribution, consumed by `flight-scene.ts`'s `readInputs`. */
export interface PointerFrame {
  readonly firing: boolean;
  /** Throttle change accumulated since the last read, already sign-correct and unclamped. */
  readonly throttleDelta: number;
}

export const NO_POINTER_INPUT: PointerFrame = { firing: false, throttleDelta: 0 };

export interface PointerControls {
  /** Reads and *clears* the accumulated wheel travel; the trigger state is level-triggered and persists. */
  frame(): PointerFrame;
  dispose(): void;
}

/**
 * Only the primary button is the trigger. The secondary one is free look and
 * is handled in `free-look-input.ts`, deliberately not here: what that file
 * produces is a camera and must never join a `PointerFrame`, which is a sim
 * input on its way to `readFlightInputs`.
 */
const PRIMARY_BUTTON = 0;

export function createPointerControls(): PointerControls {
  let firing = false;
  let throttleDelta = 0;

  function overViewport(event: Event): boolean {
    return isViewportPointerTarget(classChainOf(event.target));
  }

  function onPointerDown(event: PointerEvent): void {
    if (event.button !== PRIMARY_BUTTON || !overViewport(event)) return;
    firing = true;
  }

  /** Released anywhere, including outside the window — a trigger stuck down by a drag that left the page is worse than a missed shot. */
  function onPointerUp(event: PointerEvent): void {
    if (event.button !== PRIMARY_BUTTON) return;
    firing = false;
  }

  function onBlur(): void {
    firing = false;
  }

  function onWheel(event: WheelEvent): void {
    if (!overViewport(event)) return;
    // The viewport owns the wheel while flying; without this the page scrolls
    // under the canvas on any layout tall enough to have a scrollbar.
    event.preventDefault();
    throttleDelta += throttleWheelDelta(event.deltaY, event.deltaMode);
  }

  window.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("pointerup", onPointerUp);
  window.addEventListener("pointercancel", onPointerUp);
  window.addEventListener("blur", onBlur);
  window.addEventListener("wheel", onWheel, { passive: false });

  return {
    frame() {
      const delta = throttleDelta;
      throttleDelta = 0;
      return { firing, throttleDelta: delta };
    },
    dispose() {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("wheel", onWheel);
      firing = false;
      throttleDelta = 0;
    },
  };
}
